import type { Project } from '@/lib/mockData'
import {ArrowUpRight, Users} from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import CurvePriceChart from '@/components/CurvePriceChart'
import ExplorerLink from '@/components/ExplorerLink'
import FundingProgress from '@/components/FundingProgress'
import PresaleEndsCountdown from '@/components/PresaleEndsCountdown'
import StatusBadge from '@/components/StatusBadge'
import WatchButton from '@/components/WatchButton'
import { getBondingInfo } from '@/lib/bondingProgress'
import { formatCurrency, formatNumber, formatQuoteAmount, getProjectCountdownLabel } from '@/lib/format'
import { generateCurveHistory } from '@/lib/priceHistory'
import { clampPresaleLpPct, creatorPctFromLp, PRESALE_LP_DEFAULT_PCT } from '@/lib/protocol'
import { useTokenImage } from '@/lib/projectLinks'
import { tokenSharePath } from '@/lib/shareSlug'

interface ProjectCardProps {
  project: Project
}

function ProjectCard({ project }: ProjectCardProps) {
  const imageUrl = useTokenImage(
    [project.contractAddress, project.shareSlug, project.id, project.tokenAddress],
    project.imageUrl,
  )
  const isLaunchpad = project.type === 'launchpad'
  const accent = isLaunchpad ? 'lime' : 'orange'
  const detailPath = tokenSharePath(project.shareSlug, project.id)
  const cap = isLaunchpad ? project.goal : (project.hardCap ?? project.goal)
  const cardHoverClass = isLaunchpad
    ? 'hover:border-lime-400/40 hover:shadow-[0_0_32px_rgba(163,230,53,0.08)]'
    : 'hover:border-orange-400/40 hover:shadow-[0_0_32px_rgba(251,146,60,0.08)]'

  const bondingInfo = useMemo(() => getBondingInfo(project), [project])
  const curveHistory = useMemo(() => {
    if (!isLaunchpad)
      return []
    return generateCurveHistory(project.id, project.basePrice ?? 0, project.priceIncrement ?? 0, project.supply ?? 0, 12)
  }, [project, isLaunchpad])
  const navigate = useNavigate()

  return (
    <article
      role="link"
      tabIndex={0}
      onClick={() => navigate(detailPath)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          navigate(detailPath)
        }
      }}
      aria-label={`View ${project.name} details`}
      className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.035] backdrop-blur-md transition-all hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400/50 sm:rounded-3xl ${cardHoverClass}`}
    >
      <div className={`relative aspect-square w-full overflow-hidden sm:aspect-[4/3] ${
        isLaunchpad ? 'bg-lime-400/10' : 'bg-orange-400/10'
      }`}
      >
        {imageUrl
          ? (
              <img
                src={imageUrl}
                alt={`${project.name} token art`}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              />
            )
          : (
              <div
                className={`flex h-full w-full items-center justify-center text-xl font-bold sm:text-3xl ${
                  isLaunchpad ? 'text-lime-400' : 'text-orange-400'
                }`}
                aria-hidden="true"
              >
                {project.ticker.slice(0, 2)}
              </div>
            )}
        <div className="absolute right-2 top-2 flex items-center gap-1.5 sm:right-3 sm:top-3 sm:gap-2">
          <WatchButton projectId={project.id} projectName={project.name} compact />
          <StatusBadge status={project.status} />
        </div>
      </div>

      <div className="p-3 sm:p-5">
      <div className="min-w-0">
        <h3 className="truncate text-sm font-semibold leading-tight text-white sm:text-base">{project.name}</h3>
        <p className="truncate text-[11px] text-zinc-500 sm:text-xs">
          $
          {project.ticker}
          {' '}
          ·
          {' '}
          {project.category}
        </p>
        {project.contractAddress
          ? (
              <div className="mt-1 hidden sm:block">
                <ExplorerLink
                  address={project.contractAddress}
                  label="On-chain"
                  className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                    isLaunchpad ? 'text-lime-400 hover:text-lime-300' : 'text-orange-400 hover:text-orange-300'
                  }`}
                />
              </div>
            )
          : null}
      </div>

      <p className="mt-3 hidden line-clamp-2 text-sm text-zinc-400 sm:block">
        {project.tagline}
      </p>

      <div className="mt-2 block sm:mt-3">
        {isLaunchpad && curveHistory.length > 0
          ? (
              <div className="hidden overflow-hidden rounded-xl border border-white/5 bg-black/30 sm:block">
                <CurvePriceChart points={curveHistory} accent="lime" height={56} compact />
              </div>
            )
          : null}

        <div className="mt-2 sm:mt-4">
          <FundingProgress raised={project.raised} goal={cap} accent={accent} unit={project.quote === 'eth' ? 'eth' : 'usd'} />
        </div>

      {isLaunchpad
        ? (
            <div className="mt-2 hidden items-center justify-between text-[11px] sm:mt-3 sm:flex">
              <span className={bondingInfo.isBonded ? 'font-medium text-lime-400' : 'text-zinc-500'}>
                {bondingInfo.isBonded ? 'Bonded — open trading' : `${bondingInfo.progressPct.toFixed(0)}% to bonding`}
              </span>
              {!bondingInfo.isBonded
                ? (
                    <div className="h-1 w-16 overflow-hidden rounded-full bg-zinc-900">
                      <div className="h-full rounded-full bg-lime-400" style={{ width: `${bondingInfo.progressPct}%` }} />
                    </div>
                  )
                : null}
            </div>
          )
        : (
            <div className="mt-2 hidden items-center justify-between text-[11px] text-zinc-500 sm:mt-3 sm:flex">
              <span>
                {clampPresaleLpPct(project.lpPct ?? PRESALE_LP_DEFAULT_PCT)}
                % LP
              </span>
              <span>
                {creatorPctFromLp(project.lpPct ?? PRESALE_LP_DEFAULT_PCT)}
                % creator
              </span>
            </div>
          )}

      <div className="mt-2 flex items-center justify-between gap-2 border-t border-white/10 pt-2 text-[10px] text-zinc-500 sm:mt-4 sm:pt-4 sm:text-xs">
        <span className="flex min-w-0 items-center gap-1 sm:gap-1.5">
          <Users className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
          <span className="truncate">
            {formatNumber(project.participants)}
            <span className="hidden sm:inline"> participants</span>
          </span>
        </span>
        {project.type === 'presale'
          ? (
              <PresaleEndsCountdown
                endsAt={project.endsAt}
                startsAt={project.startsAt}
                status={project.status}
                className={`min-w-0 truncate ${project.status === 'upcoming' ? 'font-semibold text-lime-400' : 'font-medium text-orange-300'}`}
              />
            )
          : (() => {
              const countdownLabel = getProjectCountdownLabel(project)
              if (!countdownLabel)
                return null
              const isUpcomingLiveIn = project.status === 'upcoming'
              return (
                <span className={`min-w-0 truncate ${isUpcomingLiveIn ? 'font-semibold text-lime-400' : ''}`}>
                  {countdownLabel}
                </span>
              )
            })()}
      </div>

      <div className="mt-4 hidden items-center justify-between sm:flex">
        <span className="text-xs text-zinc-500">
          {isLaunchpad ? `Price ${formatCurrency(project.currentPrice ?? 0)}` : `Goal ${formatQuoteAmount(cap, project.quote)}`}
        </span>
        <span
          className={`inline-flex items-center gap-1 text-sm font-medium ${
            isLaunchpad ? 'text-lime-400' : 'text-orange-400'
          }`}
        >
          View
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
        </span>
      </div>
      </div>
      </div>
    </article>
  )
}

export default ProjectCard
